/**
 * Field-level help for creating / editing a SignalBridge store.
 *
 * Shown from the ``?`` icon next to each label on Stores → + Store
 * and on Connect a store (wizard). Keep ``where`` concrete — users
 * open this to find the Shopify / GoKwik screen that has the value.
 */
export const STORE_FIELD_HELP = {
  pixel_account: {
    title: 'Dataset',
    body: 'The Meta dataset this store should send events to. Optional if you only use Google Ads for now.',
    where: 'Add a dataset first under SignalBridge → Datasets, using the numeric Dataset ID (Pixel ID) from Meta Events Manager → Data sources.',
  },
  platform: {
    title: 'Platform',
    body: 'Which checkout actually takes the order. Shopify and GoKwik use different webhook signatures.',
    where: 'Choose Shopify if orders live in Shopify Admin. Choose GoKwik if GoKwik is the checkout layer.',
  },
  store_domain: {
    title: 'Store domain',
    body: 'The store’s technical hostname. Used for Admin API calls and webhook URLs — not the pretty website address.',
    where: 'Shopify: the *.myshopify.com host from your Admin URL (Settings → Domains). Canonical example: a00cny-n6.myshopify.com. Alias example: janayush.myshopify.com. GoKwik: the merchant host shown in the GoKwik dashboard.',
  },
  public_domain: {
    title: 'Public domain',
    body: 'The customer-facing shop URL. Used as event_source_url only when an incoming event has no landing page.',
    where: 'Your live website including https:// — e.g. https://yourshop.com. In Shopify: Settings → Domains → the primary customer domain.',
  },
  gokwik_webhook_url: {
    title: 'GoKwik webhook URL',
    body: 'A separate path from the Shopify webhook. Copy this one into GoKwik so orders, abandoned carts, and payments arrive here. Abandoned carts are sent to Meta as InitiateCheckout. GoKwik does not send an auth header.',
    where: 'GoKwik Dashboard → Kwik Checkout → Settings → Integrations → Webhooks. Enable order webhooks and Abandoned Cart Webhooks (cart state Address Added). Paste the same URL into Payment Webhooks V3 and subscribe to transaction.successful.',
  },
  shopify_webhook_url: {
    title: 'Shopify webhook URL',
    body: 'Copy this URL into the Shopify script. Register webhooks posts it to Shopify Admin. Shopify signs the request. Do not paste this URL into GoKwik.',
    where: 'On the store row, click Register webhooks, or copy it into the Shopify script.',
  },
  webhook_secret: {
    title: 'Webhook HMAC secret',
    body: 'The signing secret used to verify that inbound webhooks really came from Shopify or GoKwik. Encrypted at rest; leave blank on edit to keep the stored value. For a store-owned Dev Dashboard app this is that app’s Client secret.',
    where: 'Shopify: Dev Dashboard app → Settings → Client secret. GoKwik: HMAC secret from the merchant dashboard. Abandoned-cart and Payment Webhooks V3 use this same store webhook URL. Payment V3 signs data.hmac inside the JSON (HMAC-SHA512), not an HTTP header.',
  },
  api_access_token: {
    title: 'Shopify Admin token / GoKwik App Secret',
    body: 'Shopify: lets SignalBridge register webhooks and sync orders from Shopify Admin (hourly 12h, nightly 48h, and the Sync orders button). The button is not limited to 48 hours — pick 7, 30, or 60 days. Shopify Admin itself returns at most about 60 days of orders unless the app has the read_all_orders scope. For a custom app created in this store’s Dev Dashboard, SignalBridge requests a 24-hour token with Client ID + Client secret — you do not paste a static shpat_ token. GoKwik: this is the App Secret from Dashboard → Account. Together with App ID it pulls orders when a webhook was missed.',
    where: 'Shopify: JanAyush Admin → Settings → Apps → Develop apps → Build apps in Dev Dashboard → your app → Settings (Client ID and Client secret). Scopes: read_orders, read_checkouts, read_customers. GoKwik: Dashboard → Account → App Secret.',
  },
  gokwik_app_id: {
    title: 'GoKwik App ID',
    body: 'Identifies this merchant to GoKwik’s API so SignalBridge can pull order details (same idea as Shopify Admin sync). Webhooks still work without it.',
    where: 'GoKwik Dashboard → Account. Copy App ID. Paste App Secret in the token field on this same form.',
  },
  auth_mode: {
    title: 'Auth mode (GoKwik)',
    body: 'Shopify always uses HMAC. GoKwik abandoned-cart and order webhooks do not send an auth header — the webhook URL is enough. Payment Webhooks V3 sign data.hmac inside the JSON.',
    where: 'Leave this on HMAC. Use Shared token or IP allow-list only if you configured GoKwik to send that token or to call from fixed IPs.',
  },
  shared_token: {
    title: 'Shared token',
    body: 'A shared secret GoKwik sends as ?token= or the X-SignalBridge-Token header when HMAC is not used.',
    where: 'Copy it from the GoKwik merchant dashboard webhook / integration settings. Leave blank if you are using HMAC.',
  },
  is_active: {
    title: 'Active',
    body: 'Inactive stores reject inbound webhooks with 404, so tracking pauses until you turn this back on.',
    where: 'Leave this on unless you want to temporarily stop events from this store.',
  },
};
